import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Heart, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { useLanguage } from "@/i18n/LanguageContext";
import { lc } from "@/i18n/copy";

const copy = {
  en: { title: "This page doesn't exist yet", subtitle: "But the people you love do. Maybe it's a sign to go tell them something meaningful.", home: 'Back to Home', write: 'Write a Message', tagline: "Say It NOW — While They're Still Here" },
  es: { title: 'Esta página aún no existe', subtitle: 'Pero las personas que amas sí. Tal vez sea una señal para ir a decirles algo significativo.', home: 'Volver al Inicio', write: 'Escribe un Mensaje', tagline: 'Say It NOW — Mientras Aún Están Aquí' },
  pt: { title: 'Esta página ainda não existe', subtitle: 'Mas as pessoas que você ama existem. Talvez seja um sinal para dizer algo significativo a elas.', home: 'Voltar ao Início', write: 'Escrever uma Mensagem', tagline: 'Say It NOW — Enquanto Ainda Estão Aqui' },
  fr: { title: "Cette page n'existe pas encore", subtitle: "Mais les gens que vous aimez, oui. C'est peut-être un signe pour aller leur dire quelque chose de significatif.", home: "Retour à l'Accueil", write: 'Écrire un Message', tagline: "Say It NOW — Tant Qu'ils Sont Encore Là" },
  ar: { title: 'هذه الصفحة غير موجودة بعد', subtitle: 'لكن الأشخاص الذين تحبهم موجودون. ربما هذه إشارة لتذهب وتقول لهم شيئاً ذا معنى.', home: 'العودة للرئيسية', write: 'اكتب رسالة', tagline: 'Say It NOW — بينما لا يزالون هنا' },
  ja: { title: 'このページはまだ存在しません', subtitle: 'でも、あなたが愛する人は存在します。何か意味のあることを伝えに行くサインかもしれません。', home: 'ホームに戻る', write: 'メッセージを書く', tagline: 'Say It NOW — まだここにいるうちに' },
  ko: { title: '이 페이지는 아직 존재하지 않습니다', subtitle: '하지만 당신이 사랑하는 사람은 존재합니다. 의미 있는 무언가를 전하러 가라는 신호일지도 모릅니다.', home: '홈으로 돌아가기', write: '메시지 쓰기', tagline: 'Say It NOW — 아직 곁에 있을 때' },
  id: { title: 'Halaman ini belum ada', subtitle: 'Tapi orang-orang yang Anda cintai ada. Mungkin ini tanda untuk pergi dan mengatakan sesuatu yang bermakna kepada mereka.', home: 'Kembali ke Beranda', write: 'Tulis Pesan', tagline: 'Say It NOW — Selagi Mereka Masih Ada' },

  hi: { title: "This page doesn't exist yet", subtitle: "But the people you love do. Maybe it's a sign to go tell them something meaningful.", home: 'होम पर वापस', write: 'एक संदेश लिखें', tagline: "Say It NOW — While They're Still Here" },
};

const NotFound = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const c = lc(copy, language);

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-hero-gradient relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 -left-20 w-64 h-64 rounded-full bg-primary/5 blur-3xl animate-orb-pulse" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 rounded-full bg-secondary/5 blur-3xl animate-orb-pulse-alt" />
      </div>

      <motion.div
        className="relative text-center px-6 max-w-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <motion.div
          className="mb-8 flex justify-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <BrandLogo size="lg" />
        </motion.div>

        <motion.p
          className="text-8xl font-heading font-bold text-primary/20 mb-2"
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          404
        </motion.p>

        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-3">{c.title}</h1>
        <p className="text-base text-muted-foreground mb-8 max-w-sm mx-auto">{c.subtitle}</p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button asChild size="lg" className="press-effect">
            <Link to="/"><Home className="mr-2 h-4 w-4" />{c.home}</Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="press-effect">
            <Link to="/create"><Heart className="mr-2 h-4 w-4" />{c.write}</Link>
          </Button>
        </div>

        <motion.p
          className="mt-12 text-xs text-muted-foreground/60 flex items-center justify-center gap-1.5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <Heart className="h-3 w-3 text-primary/40" fill="currentColor" />
          {c.tagline}
        </motion.p>
      </motion.div>
    </div>
  );
};

export default NotFound;
